/** Where the Connect an AI tool page lives (specs 236, 241); the old path stays. */
export const EDITOR_SETTINGS_PATH = '/settings/editor';

/**
 * What an AI tool needs to be told, named once (spec 236 FR-004/005). The realm
 * accepts sign-ins only through this pre-registered client; none registers
 * itself (spec 241). Claude Code's command pins this loopback port, which every
 * Keycloak version accepts.
 */
export const MCP_CLIENT_ID = 'ai-powered-sdlc-mcp';
export const MCP_CALLBACK_PORT = 33418;
/** The app's name as an editor lists the server; never "Product X". */
export const MCP_SERVER_NAME = 'ai-powered-sdlc';

// Copy drafted by AI; awaiting the user's review (constitution).
/** The words of the Connect an AI tool page (specs 236, 241, 242). */
export const EDITOR_COPY = {
  // "AI tool", not "editor" (spec 241, GLOSSARY.md): claude.ai and Codex are
  // not editors.
  title: 'Connect an AI tool',
  // A place, as its neighbours in the rail are (spec 239); the page's heading
  // keeps saying what it is for.
  nav: 'AI tools',
  // The trust line leads (spec 242): what the tool may do is the first thing a
  // person weighs, not the last.
  intro:
    'Work on tasks, workflows and workspace files from the AI tool you already use. It signs in as you, with the access you have here and no more.',
  // The tools that have acted as this person (spec 245), above the setup once
  // there is one.
  yoursHeading: 'Your AI tools',
  yoursHint: 'Tools that have worked here as you, latest first.',
  lastUsed: (when: string) => `Last used ${when}`,
  firstConnected: (day: string) => `first connected ${day}`,
  // One Disconnect for every tool (spec 247): they all sign in through one
  // client, so none can be cut off alone.
  disconnect: 'Disconnect',
  disconnectAll: 'Disconnect all',
  disconnecting: 'Disconnecting…',
  disconnectTitle: 'Disconnect your AI tools?',
  disconnectBody: (tools: string) =>
    `${tools} lose access now. To connect one again, sign in from it. You stay signed in here.`,
  disconnected: 'Your AI tools are disconnected.',
  disconnectFailed: 'Your AI tools could not be disconnected. Try again.',
  setupHeading: 'Set up',
  toolsLabel: 'AI tools',
  tabs: {
    'claude-code': 'Claude Code',
    copilot: 'GitHub Copilot',
    codex: 'Codex',
    'claude-ai': 'claude.ai',
    other: 'Other tools',
  },
  steps: {
    add: 'Add it',
    clientId: 'Give it the client ID',
    signIn: 'Sign in',
    tryIt: 'Try it',
  },
  addressHeading: 'Server address',
  // True on every tab, claude.ai's included where it cannot connect.
  addressHint: 'The address an AI tool connects to.',

  claudeAdd: 'Run this in a terminal:',
  claudeSignIn:
    'Run /mcp in Claude Code and choose Authenticate. Your browser opens this app’s sign-in.',

  vsCodeInstall: 'Install in VS Code',
  vsCodeInsidersInstall: 'Install in VS Code Insiders',
  // A link like this can be made by anyone, for any address: the person checks
  // the one VS Code shows before installing (research note, security §9).
  vsCodeCheck: (address: string) =>
    `Opens VS Code’s install page. Check that it shows ${address}, then choose Install.`,
  copilotSignIn:
    'In Copilot Chat, switch to Agent mode and start ai-powered-sdlc from its tools. Your browser opens this app’s sign-in.',
  copilotByHand: 'Copilot CLI, or set it up by hand',
  copilotHint:
    'Add this to ~/.copilot/mcp-config.json, creating the file if there is none. The Copilot CLI and VS Code both read it.',
  copilotCliSignIn: 'Then run /mcp auth ai-powered-sdlc in the Copilot CLI.',
  copilotPolicy:
    'On a Copilot Business or Enterprise plan, your organization’s admin must first turn on the “MCP servers in Copilot” policy.',

  codexAdd:
    'Run this in a terminal. It opens this app’s sign-in in your browser straight away.',
  codexSignIn:
    'Sign in there. To sign in again later, run codex mcp login ai-powered-sdlc.',
  codexShared:
    'The Codex CLI, its IDE extension and the ChatGPT desktop app share this setup.',

  claudeAiWho:
    'Add it to claude.ai as a custom connector. On a Team or Enterprise plan an Owner adds it once for the whole organization; on Pro or Max you add it yourself. It then works in Claude Desktop and mobile too.',
  claudeAiOpen: 'Open in claude.ai',
  claudeAiOpenForOrganization: 'Add for your organization',
  claudeAiCheck: (address: string) =>
    `The dialog opens filled in: check that the URL is ${address}.`,
  claudeAiClientId:
    'Under OAuth client, choose “Use your own OAuth client”, enter this client ID and leave the client secret empty.',
  claudeAiSignIn:
    'Add it, then choose Connect. Your browser opens this app’s sign-in.',
  claudeAiReach:
    'claude.ai connects from Anthropic’s servers, not from your computer, so this works only where this app is reachable from the internet.',
  // Shown rather than hidden (spec 242): a person looking for claude.ai finds
  // why it is not offered here.
  claudeAiUnreachable: (address: string) =>
    `claude.ai connects from Anthropic’s servers, so it needs this app on a public HTTPS address. ${address} is not one: claude.ai cannot connect to this environment.`,

  otherAdd: 'For a tool that reads an MCP config file:',
  otherSignIn: (clientId: string) =>
    `If the tool asks how to sign in, the client ID is ${clientId}.`,
  otherUntried:
    'JetBrains, Visual Studio and other tools have not been tried here; one that cannot be given a client ID cannot sign in.',

  // How each tool offers the server's prompts and resources (spec 242): Claude
  // Code (specs 198, 200), VS Code's Copilot Chat (its MCP docs). Codex and
  // claude.ai document tools only, so they are asked in words.
  tryCommands: 'Type / for its commands, or @ to attach a task or a file:',
  tryCopilot:
    'In Copilot Chat, type / for its commands, or attach a task or a file with Add Context > MCP Resources:',
  tryWords: 'Ask in your own words:',
  whatsWaiting: 'What needs you across your workspaces',
  pickUpTask: 'Where a task stands and what to do next',
  mention: 'A task or a file, chosen as you type',
  asks: [
    'What needs me in my workspaces?',
    'Pick up the next task on my board.',
    'Why did my last run fail?',
  ],

  expectHeading: 'What to expect',
  canHeading: 'What it can do',
  can: [
    'Read the work in a workspace and pick up a task with its runs, comments and results.',
    'Create, move, comment on, stop, rerun and hide tasks.',
    'Read and change workspace files, and see their history.',
    'Import workflows and change them through a draft you publish.',
    'Set up triggers, and set or remove secrets — it never sees a secret’s value.',
    'Hand you a download link for a large file or a task’s whole folder.',
  ],
  rulesHeading: 'What holds',
  rules: [
    'It acts as you, with your access in each workspace.',
    'What it does shows under your name, as if you had done it here.',
    'Deleting a workspace or deciding who can enter one stays in this app.',
    'To cut your AI tools off, choose Disconnect under Your AI tools, or remove ai-powered-sdlc from a tool.',
  ],
  loading: 'Checking whether AI tools can connect',
  offHeading: 'Not available here',
  off: 'AI tools cannot connect to this environment: its MCP server is switched off. An administrator can turn it on.',
  checkFailed: 'Whether AI tools can connect could not be checked.',
  retry: 'Try again',
  copy: (what: string) => `Copy ${what}`,
  copied: (what: string) => `Copied ${what}`,
  copyFailed: (what: string) => `Could not copy ${what}`,
};

// Copy drafted by AI; awaiting the user's review (constitution).
/**
 * The hints around the app that copy an ask for a person's AI tool (spec 248),
 * or, for someone with no tool yet, lead to setting one up.
 */
export const AI_HELP_COPY = {
  paletteEntry: 'Go to AI tools',
  taskCopy: 'Copy for your AI tool',
  setUp: 'Set up an AI tool',
  runCopy: 'Ask your AI tool why',
  runSetUp: 'Set up an AI tool to ask why',
  workflowCopy: {
    before: 'Or ',
    label: 'describe it to your AI tool',
    after: ' and let it draft one.',
  },
  workflowSetUp: {
    before: 'Or let an AI tool draft it: ',
    label: 'set one up',
    after: '.',
  },
  copied: 'Copied',
  copiedToast: 'Copied. Paste it into your AI tool.',
  notCopied: 'Not copied',
  notCopiedHint: 'Select it here and copy it yourself:',
} as const;
